<template>
  <TsSection
    v-if="authStore.is_authenticated && product_codes.length > 0"
  >
    <TsTypography as="h2" variant="h2" append-class="mb-2" align="left">
      {{ useTranslation("saveList", "Kluslijst") }}
    </TsTypography>

    <div v-if="savelistStore.user_savelists_state.status === 'loading'">
      <TsTypography variant="h3" append-class="mb-2" align="left">
        {{ useTranslation("loading", "Laden...") }}
      </TsTypography>
    </div>

    <div>
      <div v-if="savelistStore.selected_savelist_item_state.status === 'idle'">
        <TsProductCardTransition
          class="mt-2"
          :rows="1"
          :cols="5"
        ></TsProductCardTransition>
      </div>
      <div
        v-if="savelistStore.selected_savelist_item_state.status === 'loading'"
      >
        <TsSavelistCarouselTransition
          class="mt-2"
          :rows="1"
          :cols="5"
        ></TsSavelistCarouselTransition>
      </div>
      <div
        v-if="savelistStore.selected_savelist_item_state.status === 'success'"
      >
        <TsCarouselV2 append-class="lg:px-0 px-0">
          <TsCarouselSlide append-class="flex-[0_0_calc(100%/1.4)] md:flex-[0_0_calc(100%/3)] lg:flex-[0_0_calc(100%/4)] xl:flex-[0_0_calc(100%/5)]" v-for="(product,index) in sliced_products" :key="product.code">
            <TsCardProduct
              :data-testid="`savelist-${product.code}`"
              productContextType="savelist" 
              :product="product"
              product-layout="mobile-grid"
              append-class="min-w-[238px] max-w-[238px]"
              :is-compare="false"
              hide-buy-now
              button-outlined
              :isMoveToTrolley="true"
              @click="$emit('product-click', product.code);logEcommerceEvent(product, index);"
            />
          </TsCarouselSlide>
        </TsCarouselV2>
        <NuxtLinkLocale to="/account/saved-lists" v-if="products_data.length > 5">
          <span
            class="hover:underline font-semibold text-info flex justify-center"
            @click="updateSaveListTab"
          >
            {{ useTranslation("viewMore", "Bekijk meer") }}
          </span>
        </NuxtLinkLocale>
      </div>
    </div>
  </TsSection>
</template>

<script setup lang="ts">
import { useTranslation } from "~/composables/useTranslation";
import { ProductService } from "~/services/product.service";
import type { Product } from "~/types/ecom/product/product.type";

const { $customClientErrorHandler } = useNuxtApp();

const TsProductCardTransition = defineAsyncComponent(
    () => import('~/components/organisms/TsProductCardTransition.vue')
);
const TsSavelistCarouselTransition = defineAsyncComponent(
    () => import('~/components/organisms/TsProductCardTransition.vue')
);
const TsCardProduct = defineAsyncComponent(
    () => import('~/components/organisms/TsCardProduct.vue')
);

const TsCarouselSlide = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselSlide.vue')
);

const TsCarouselV2 = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselV2.vue')
);

const savelistStore = useSavelistStore();
const { user_savelists } = storeToRefs(savelistStore);
const authStore = useAuthStore();
const { $customClientWarningHandler } = useNuxtApp();

const products_data = ref<Product[]>([]);

const sliced_products = computed(() => products_data.value.slice(0, 5));

const product_codes = computed<any[]>(() => {
  const save_for_later_list = user_savelists.value.find(
    (savelist: any) => savelist.name === "Bewaar voor later"
  );
  return save_for_later_list
    ? save_for_later_list.items.map((item: any) => item.product_code)
    : [];
});

const stopTrolleySaveListWatch = watch(product_codes, async (updatedProductCodes) => {
  if (!updatedProductCodes.length) return;
  const warningMeta = {updatedProductCodes, trolleySaveListSection: true}
  try {    
    const productResponse = await new ProductService().getProducts(
      updatedProductCodes,
      true
    );
    if(productResponse?.length) products_data.value = productResponse; 
    else $customClientWarningHandler('high','save for later product data fetch failed',warningMeta);;
  } catch (e) {
    products_data.value = [];
    let errorMeta = {productCode: updatedProductCodes, trolleySaveListError: true}

    $customClientErrorHandler(
        e,
        'high',
        'Trolley Error - trying to fetch updated trolley products using stopTrolleySaveListWatch()',
        errorMeta
    );
  }
})

onMounted(async () => {
  if (!savelistStore.fetching_in_progress) {
  await savelistStore.getUserSaveLists();
  }
});

const updateSaveListTab = () => {
  savelistStore.updateCurrentSavelist(
    user_savelists.value[1].name,
    user_savelists.value[1].id,
    user_savelists.value[1].items,
    user_savelists.value[1].internal,
    1,
    user_savelists.value[1].is_default
  );
};

onUnmounted(() => {
  stopTrolleySaveListWatch();
})
</script>
